import React, { useRef, useEffect, useState } from 'react';
import { VideoMetadata, azureBlobService } from '../services/azureBlobService';
import '../styles/VideoPlayer.css';

interface VideoPlayerProps {
  video: VideoMetadata;
  onClose: () => void;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ video, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoUrl, setVideoUrl] = useState<string>(video.url);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [loadingProgress, setLoadingProgress] = useState<string>('Initializing...');
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    
    // Load video data from IndexedDB if it's a local file
    const loadVideoData = async () => {
      if (video.url.startsWith('video_file_')) {
        setLoading(true);
        setError('');
        setLoadingProgress('Loading video from storage...');
        
        try {
          const startTime = Date.now();
          const url = await azureBlobService.getVideoUrl(video.url);
          const loadTime = ((Date.now() - startTime) / 1000).toFixed(1);
          
          if (isMounted) {
            setVideoUrl(url);
            blobUrlRef.current = url;
            console.log(`✅ Video loaded in ${loadTime}s`);
          }
        } catch (error) {
          if (isMounted) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            console.error('Failed to load video:', error);
            setError(`Failed to load video: ${errorMsg}`);
          }
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
      }
    };
    
    loadVideoData();

    // Focus on the video element when component mounts
    if (videoRef.current) {
      videoRef.current.focus();
    }
    
    // Cleanup: Revoke blob URL to free memory
    return () => {
      isMounted = false;
      if (blobUrlRef.current && blobUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, [video.url]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
    if (e.key === ' ') {
      e.preventDefault();
      if (videoRef.current) {
        if (videoRef.current.paused) {
          videoRef.current.play();
        } else {
          videoRef.current.pause();
        }
      }
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDownload = async () => {
    try {
      // Check if it's a local video stored in IndexedDB
      if (video.url.startsWith('video_file_')) {
        // Get the blob URL if not already loaded
        const downloadUrl = videoUrl.startsWith('blob:') ? videoUrl : await azureBlobService.getVideoUrl(video.url);
        
        // Create a temporary anchor element to trigger download
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = video.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        // For Azure-hosted videos, open in new tab
        window.open(video.url, '_blank');
      }
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download video. Please try again.');
    }
  };

  return (
    <div className="video-player-overlay" onKeyDown={handleKeyDown} tabIndex={0}>
      <div className="video-player-container">
        <div className="player-header">
          <div className="player-title">
            <h2>{video.name}</h2>
            <button onClick={onClose} className="close-button" title="Close (Esc)">
              ✕
            </button>
          </div>
        </div>

        <div className="player-content">
          <div className="video-wrapper">
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>{loadingProgress}</p>
                <p className="loading-hint">Large videos may take a moment to load</p>
              </div>
            ) : error ? (
              <div className="error-container">
                <p className="error-text">❌ {error}</p>
                <button onClick={onClose} className="action-btn close-btn">
                  ← Back to List
                </button>
              </div>
            ) : (
              <video
                ref={videoRef}
                src={videoUrl}
                controls
                autoPlay
                className="video-element"
                onError={(e) => {
                  const videoElement = e.target as HTMLVideoElement;
                  const errorMsg = `Video playback error: ${videoElement.error?.message || 'Unknown error'}`;
                  console.error(errorMsg);
                  setError(errorMsg);
                }}
              >
                Your browser does not support the video tag.
              </video>
            )}
          </div>

          <div className="video-details">
            <div className="detail-item">
              <span className="detail-label">File Size:</span>
              <span className="detail-value">{formatFileSize(video.size)}</span>
            </div>
            
            <div className="detail-item">
              <span className="detail-label">Type:</span>
              <span className="detail-value">{video.type}</span>
            </div>
            
            <div className="detail-item">
              <span className="detail-label">Uploaded:</span>
              <span className="detail-value">{formatDate(video.uploadedAt)}</span>
            </div>
            
            <div className="detail-item">
              <span className="detail-label">Uploaded by:</span>
              <span className="detail-value">{video.uploadedBy}</span>
            </div>
          </div>
        </div>

        <div className="player-actions">
          <button
            onClick={handleDownload}
            className="action-btn download-btn"
          >
            📥 Download
          </button>
          
          <button
            onClick={() => {
              const linkToCopy = video.url.startsWith('video_file_') 
                ? 'Video is stored locally - share not available' 
                : video.url;
              
              if (video.url.startsWith('video_file_')) {
                alert('This video is stored locally and cannot be shared via link.');
              } else {
                navigator.clipboard.writeText(linkToCopy);
                alert('Link copied to clipboard!');
              }
            }}
            className="action-btn share-btn"
            disabled={video.url.startsWith('video_file_')}
          >
            🔗 Copy Link
          </button>
          
          <button onClick={onClose} className="action-btn close-btn">
            ← Back to List
          </button>
        </div>

        <div className="player-controls-help">
          <p>
            <strong>Keyboard shortcuts:</strong> 
            Space to play/pause • Esc to close
          </p>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;